import React, { useState, useEffect } from 'react';
import posthog from 'posthog-js';
import { fetchUserReferrals, UserReferralsResponse } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import './ReferralDashboard.css';

const ReferralDashboard: React.FC = () => {
  const { user } = useAuth();
  const [referralData, setReferralData] = useState<UserReferralsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [copySuccess, setCopySuccess] = useState(false);

  useEffect(() => {
    const loadReferralData = async () => {
      if (!user?.issuer) return;

      try {
        setLoading(true);
        const data = await fetchUserReferrals(user.issuer);
        setReferralData(data);
        setError(null);
      } catch (err) {
        console.error('Failed to load referral data:', err);
        setError('Failed to load referral data');
      } finally {
        setLoading(false);
      }
    };

    loadReferralData();
  }, [user?.issuer]);

  const handleCopyReferralLink = () => {
    if (!referralData?.codes || referralData.codes.length === 0) return;

    const mostRecentCode = referralData.codes[0];
    const referralLink = `${window.location.origin}/?ref=${mostRecentCode.code}`;
    
    navigator.clipboard.writeText(referralLink).then(() => {
      setCopySuccess(true);
      try { posthog.capture('engagement:referral_link_copied', { code: mostRecentCode.code }); } catch {}
      setTimeout(() => setCopySuccess(false), 2000);
    });
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
  };

  const getStatusBadge = (referral: any) => {
    if (referral.reward_status === 'credited') {
      return <span className="status-badge status-credited">✓ Added</span>;
    } else if (referral.reward_status === 'pending') {
      return <span className="status-badge status-pending">⏳ Pending</span>;
    } else {
      return <span className="status-badge status-failed">✗ Failed</span>;
    }
  };

  if (loading) {
    return <div className="referral-dashboard-loading">Loading referral data...</div>;
  }

  if (error) {
    return <div className="referral-dashboard-error">{error}</div>;
  }

  if (!referralData) {
    return <div className="referral-dashboard-error">No referral data available</div>;
  }

  const mostRecentCode = referralData.codes.length > 0 ? referralData.codes[0] : null;
  const pendingCount = referralData.referrals.filter(r => !r.first_parlay_placed).length;
  const creditedCount = referralData.referrals.filter(r => r.reward_status === 'credited').length;

  return (
    <div className="referral-dashboard">
      <h2 className="section-title">Referral Stats</h2>

      {/* Stats Overview */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-label">Total Earned</div>
          <div className="stat-value">{referralData.total_earned.toFixed(2)} TPC</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Successful Referrals</div>
          <div className="stat-value">{creditedCount}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Pending Referrals</div>
          <div className="stat-value">{pendingCount}</div>
        </div>
      </div>

      {/* Share Your Code */}
      {mostRecentCode && (
        <div className="share-code-section">
          <h3 className="section-title">Share Your Referral Code</h3>
          <div className="share-code-card">
            <div className="referral-code-display">
              <span className="code-label">Your Code:</span>
              <span className="code-value">{mostRecentCode.code}</span>
            </div>
            <button 
              className="copy-link-button"
              onClick={handleCopyReferralLink}
            >
              {copySuccess ? '✓ Copied!' : 'Copy Referral Link'}
            </button>
            <div className="code-info">
              <span>
                Used: {mostRecentCode.successful_referrals}/{mostRecentCode.max_uses == null ? '∞' : mostRecentCode.max_uses}
              </span>
              <span>Expires: {formatDate(mostRecentCode.expires_at)}</span>
            </div>
          </div>
        </div>
      )}

      {/* Referral History */}
      {referralData.referrals.length > 0 ? (
        <div className="referral-history-section">
          <h3 className="section-title">Referral History</h3>
          <div className="referral-table">
            <div className="table-header">
              <div className="table-cell">User</div>
              <div className="table-cell">Code</div>
              <div className="table-cell">Status</div>
              <div className="table-cell">Reward</div>
            </div>
            {referralData.referrals.map((referral, index) => (
              <div key={index} className="table-row">
                <div className="table-cell">{referral.referred_email}</div>
                <div className="table-cell">{referral.referral_code}</div>
                <div className="table-cell">{getStatusBadge(referral)}</div>
                <div className="table-cell">
                  {referral.reward_status === 'credited' 
                    ? `${referral.reward_amount} TPC`
                    : '-'
                  }
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="no-referrals">
          <p>No referrals yet. Share your code to start earning!</p>
        </div>
      )}
    </div>
  );
};

export default ReferralDashboard;

